import { decodeHtmlEntities } from '../../common/helpers/htmlEntities';
import { NewsProps } from '../../layout/news/NewsLayout.props';
import { joinUrl, toAbsoluteUrl } from './urlUtils';

export interface BuildArticleJsonLdInput {
  newsProps: NewsProps;
  siteRoot: string;
  currentPath: string;
}

const ORG_NAME = 'Warner Bros. Entertainment Inc.';

const toIsoDate = (raw: string | undefined): string | undefined => {
  if (!raw) return undefined;
  const numeric = Number(raw);
  if (!Number.isNaN(numeric) && numeric > 0) {
    const date = new Date(numeric * 1000);
    if (!Number.isNaN(date.getTime())) return date.toISOString();
  }
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) return parsed.toISOString();
  return undefined;
};

export const buildArticleJsonLd = ({
  newsProps,
  siteRoot,
  currentPath,
}: BuildArticleJsonLdInput): Record<string, unknown> => {
  const { title, subtitle, image, promoImage, date, categories } = newsProps;
  const canonicalUrl = joinUrl(siteRoot, currentPath);
  const imageSrc = image?.src || promoImage?.src;
  const imageUrl = toAbsoluteUrl(siteRoot, imageSrc);
  const datePublished = toIsoDate(date);

  const data: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: decodeHtmlEntities(title),
    mainEntityOfPage: canonicalUrl,
    url: canonicalUrl,
    author: { '@type': 'Organization', name: ORG_NAME },
    publisher: {
      '@type': 'Organization',
      name: ORG_NAME,
      logo: { '@type': 'ImageObject', url: toAbsoluteUrl(siteRoot, '/assets/logo.png') },
    },
  };

  if (subtitle) data.description = decodeHtmlEntities(subtitle);
  if (imageUrl) data.image = imageUrl;
  if (datePublished) data.datePublished = datePublished;
  if (categories && categories.length > 0) data.articleSection = categories;

  return data;
};
